import { useCallback, useId, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import NotFoundPage from "components/error/NotFoundPage";
import { SavedFilterView, SortStateDescriptor } from "components/Filters/types";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import useErrorHandle from "hooks/useErrorHandle";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";

import { ANALYTICS, CONTEXTS_LIMIT, CONTEXTS_LIMIT_STORAGE_KEY } from "./constants";
import FiltersLayout from "./FiltersLayout";
import useContexts from "./useContexts";
import ContextsPageLayout from "./PageLayout";
import ContextsTable from "./Table";
import ContextsEmpty from "./Empty";
import useContextsAnalytics from "./useContextsAnalytics";
import useContextsStore from "./useContextsStore";

const Contexts = () => {
  const [currentSavedView, setCurrentSavedView] = useState<SavedFilterView | undefined>(undefined);

  const { allItemsQty } = useContextsStore(
    useShallow((state) => ({
      allItemsQty: state.allItemsQty,
    }))
  );

  const {
    initialLoading,
    error,
    contexts,
    loadContexts,
    endCursor,
    startCursor,
    noResults,
    isEmpty,
    loadingContent,
    searchIsEmpty,
  } = useContexts();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
    resetToInitialPage: resetPaginationToInitialPage,
  } = usePagination({
    ...ANALYTICS,
    fetchItems: loadContexts,
    endCursor,
    startCursor,
    items: contexts,
    initialLimit: CONTEXTS_LIMIT,
    itemsCount: allItemsQty,
    storageKey: CONTEXTS_LIMIT_STORAGE_KEY,
  });

  const trackAnalytics = useContextsAnalytics();

  const handleSortCallback = useCallback(
    (descriptor: SortStateDescriptor) => {
      trackAnalytics("Sort by column", { ...descriptor });
      resetPaginationToInitialPage();
    },
    [trackAnalytics, resetPaginationToInitialPage]
  );

  const ErrorContent = useErrorHandle(error);

  const titleId = useId();

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return (
      <ContextsPageLayout hideActions>
        <PageLayoutSkeleton />
      </ContextsPageLayout>
    );
  }

  if (noResults) {
    return <NotFoundPage />;
  }

  return (
    <ContextsPageLayout>
      <FiltersLayout
        currentSavedView={currentSavedView}
        setCurrentSavedView={setCurrentSavedView}
        titleId={titleId}
      >
        {isEmpty && <ContextsEmpty hasNoSearchResult={searchIsEmpty} />}

        {!isEmpty && (
          <>
            <ContextsTable
              contexts={contexts}
              loading={loadingContent}
              rowsPerPage={rowsPerPage}
              sortCallback={handleSortCallback}
            />
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={allItemsQty}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </FiltersLayout>
    </ContextsPageLayout>
  );
};

export default Contexts;
